import { useMemo, type ReactNode } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { BrainCircuitIcon } from 'lucide-react';
import { useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';

import { useAiAgentsVariables } from './useAiAgentsVariables';
import { useComparisonMode } from '../../../hooks/useComparisonMode';

export interface AiAgentHallucinationRow {
  id: string;
  name: string;
  isMissing: boolean;
  icon: ReactNode;
  totalMessages: number;
  previousTotalMessages: number;
  detectedPercentLabel: string;
  detectedFactual: number;
  detectedJailbreak: number;
  detectedOutOfScope: number;
}

export interface AiAgentsHallucinationsRow {
  brain_parent_id: string;
  total_responses: number | null;
  detected_responses: number | null;
  jailbreak: number | null;
  out_of_scope: number | null;
  major_hallucination: number | null;
  minor_hallucination: number | null;
}

export type AiAgentHallucinationCounts = Pick<
  AiAgentHallucinationRow,
  'detectedFactual' | 'detectedJailbreak' | 'detectedOutOfScope'
>;

/**
 * Detected counts include every evaluator catch on any attempt (retried or not),
 * so buckets overlap and must never be summed across types. Major and minor
 * hallucinations merge into one "factual" figure.
 */
export const mapHallucinationCounts = (
  row: AiAgentsHallucinationsRow
): AiAgentHallucinationCounts => ({
  detectedFactual: (row.major_hallucination ?? 0) + (row.minor_hallucination ?? 0),
  detectedJailbreak: row.jailbreak ?? 0,
  detectedOutOfScope: row.out_of_scope ?? 0,
});

/**
 * Share of an agent's responses the evaluator flagged at least once, shown next
 * to the total in the table. Uses detected_responses (turns with >=1 detection,
 * deduplicated so a multi-flag turn counts once) over total responses.
 *
 * Detections are rare, so most real rates sit below 1% and integer rounding
 * would print them as "0%" — the same label an agent with no detections gets.
 * Any nonzero rate that rounds to zero renders as "<1%" instead, so "0%" only
 * ever means zero detections.
 */
export const formatDetectedPercentLabel = (detected: number, total: number): string => {
  if (detected <= 0 || total <= 0) {
    return '0%';
  }
  const rounded = Math.round((detected / total) * 100);
  return rounded === 0 ? '<1%' : `${rounded}%`;
};

const HALLUCINATION_FIELDS = `
  brain_parent_id
  total_responses
  detected_responses
  jailbreak
  out_of_scope
  major_hallucination
  minor_hallucination
`;

const GET_AI_AGENTS_FLAGGED_RESPONSES_BY_AGENT = gql`
  query AiAgentsFlaggedResponsesByAgent(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_flagged_responses_by_agent(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${HALLUCINATION_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_FLAGGED_RESPONSES_BY_AGENT_COMPARISON = gql`
  query AiAgentsFlaggedResponsesByAgentComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_flagged_responses_by_agent(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${HALLUCINATION_FIELDS}
    }
  }
`;

export const useAiAgentsHallucinations = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { brains, listStatus: brainsStatus } = useBrains();

  const { variables: currentVariables, skip } = useAiAgentsVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useAiAgentsVariables(comparisonDates);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery<{ rows: AiAgentsHallucinationsRow[] }>(GET_AI_AGENTS_FLAGGED_RESPONSES_BY_AGENT, {
    variables: currentVariables,
    skip,
  });

  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery<{ rows: AiAgentsHallucinationsRow[] }>(
    GET_AI_AGENTS_FLAGGED_RESPONSES_BY_AGENT_COMPARISON,
    { variables: comparisonVariables, skip: skip || !isComparing }
  );

  const data = useMemo<AiAgentHallucinationRow[]>(() => {
    const currentRows = currentData?.rows ?? [];
    const previousByParent = new Map<string, AiAgentsHallucinationsRow>(
      (comparisonData?.rows ?? []).map((row) => [row.brain_parent_id, row])
    );
    const brainsByParent = new Map((brains ?? []).map((brain) => [brain.brain_id, brain]));

    return currentRows.map((row) => {
      const brain = brainsByParent.get(row.brain_parent_id);
      const totalMessages = row.total_responses ?? 0;
      const previousTotalMessages = previousByParent.get(row.brain_parent_id)?.total_responses ?? 0;

      return {
        id: row.brain_parent_id,
        name: brain?.name ?? row.brain_parent_id,
        isMissing: !brain && brainsStatus === 'success',
        icon: <BrainCircuitIcon />,
        totalMessages,
        previousTotalMessages,
        detectedPercentLabel: formatDetectedPercentLabel(
          row.detected_responses ?? 0,
          totalMessages
        ),
        ...mapHallucinationCounts(row),
      };
    });
  }, [currentData?.rows, comparisonData?.rows, brains, brainsStatus]);

  return {
    data,
    isLoading: currentLoading || (isComparing && comparisonLoading),
    error: currentError ?? comparisonError ?? null,
  };
};
